import { LocalizationService } from '@abp/ng.core';
import { Component, forwardRef, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormBuilder, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FilterPanelEditorConfigurationModel } from 'src/ca-shared/filter-panel/models/filter-panel-editor-configuration.model';
import { FilterPanelEditorComponent } from 'src/ca-shared/filter-panel/models/filter-panel-editor.component';
import { SelectorComponent } from 'src/ca-shared/selector/selector.module';
import { Operators } from 'src/core/models/request/operator.enum';
import { UserGroupSelectionConfigurationModel } from '../models/user-group-selection-configuration.model';
import { UserGroupDto } from 'src/core/models/user-group/user-group.dto';
import { FilterItemDto } from 'src/core/models/request/filter-item.dto';

@Component({
  selector: 'ca-user-group-selection-panel',
  templateUrl: './user-group-selection-panel.component.html',
  styleUrls: ['./user-group-selection-panel.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => UserGroupSelectionPanelComponent),
    },
  ],
})
export class UserGroupSelectionPanelComponent
  extends FilterPanelEditorComponent
  implements OnInit, ControlValueAccessor
{
  actualConfig: UserGroupSelectionConfigurationModel;

  _value: UserGroupDto[];
  userGroupSelectorOptions: any;
  userGroupFilters: FilterItemDto[];

  @ViewChild(SelectorComponent, { read: SelectorComponent })
  userGroupSelector: SelectorComponent;

  set config(value: FilterPanelEditorConfigurationModel) {
    this.actualConfig = value as UserGroupSelectionConfigurationModel;
  }

  get config(): FilterPanelEditorConfigurationModel {
    return this.actualConfig;
  }

  set value(value: any) {
    this.editorForm.patchValue({
      editor: value,
    });
  }

  get value(): any {
    return this._value;
  }

  constructor(
    private localizationService: LocalizationService,
    private operators: Operators,
    protected fb: FormBuilder,
  ) {
    super(fb);

    this.userGroupFilters = [
      {
        field: 'includePassives',
        operator: this.operators.Equals,
        value: false,
      },
    ];

    this.userGroupSelectorOptions = {
      pageSize: 5,
      queryOperator: this.operators.Contains,
      queryField: 'quickSearchTerm',
      emptyText: this.localizationService.instant('::SelectUserGroup'),
      multiple: true,
      url: UserGroupDto.apiUrl,
      currentPage: 1,
      showSelections: false,
      showClearSelections: false,
      showClearSelectionLink: true,
      selectorPosition: 'Top',
      filters: this.userGroupFilters,
    };

    this.editorForm.valueChanges.subscribe(val => {
      this._value = val.editor;
      this.onChange();
      this.onChangeCva(this._value);
    });
  }

  ngOnInit(): void {}

  reload(): void {}

  reset(): void {}

  autoSelectFirstItem(): void {}

  init() {
    super.init();
    if (this.actualConfig.autoLoad) {
      this.userGroupSelector.refresh();
    }
  }

  // ─── ControlValueAccessor (additive — lets this editor be used standalone via
  // formControlName, e.g. inside ca-filter-panel-v2, without requiring the old
  // [config] Input / dynamic-editor-factory wiring) ───────────────────────────

  writeValue(obj: UserGroupDto[] | null): void {
    this.value = obj ?? [];
  }

  registerOnChange(fn: (value: UserGroupDto[]) => void): void {
    this.onChangeCva = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCva = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.editorForm.disable({ emitEvent: false });
    } else {
      this.editorForm.enable({ emitEvent: false });
    }
  }

  private onChangeCva: (value: UserGroupDto[]) => void = () => {};
  private onTouchedCva: () => void = () => {};

  /**
   * Overrides FilterPanelEditorComponent.onChange — the base implementation reads
   * this.config.onChange, which throws when this editor is used standalone (no
   * [config] Input bound, e.g. via formControlName) since this.config is undefined.
   * Behavior is unchanged for existing [config]-driven usages.
   */
  override onChange(initial: boolean = false): void {
    if (this.actualConfig && typeof this.actualConfig.onChange === 'function' && this.emitChangeEvent) {
      this.actualConfig.onChange(this.parentFilterPanel, this.value, initial);
    }
  }
}
